<template>
  <!-- ==================== 위젯 외곽 카드 ==================== -->
  <section
    :class="[
      'dashboard-widget',
      `dashboard-widget--${widget.layout}`,
      {
        'dashboard-widget--collapsed': collapsed,
        'dashboard-widget--draggable': canDrag,
        'dashboard-widget--sort-mode': mobileSortMode
      }
    ]"
    :data-widget-id="widget.id"
    @touchstart.passive="handleTouchStart"
    @touchend.passive="handleTouchEnd"
    @touchmove.passive="handleTouchMove"
    @touchcancel.passive="handleTouchEnd"
  >
    <!-- ==================== 위젯 헤더 ==================== -->
    <header class="dashboard-widget__header">
      <button
        v-if="canDrag"
        type="button"
        class="dashboard-widget__handle widget-drag-handle"
        :aria-label="t('dashboard.widget.dragHandle')"
        :title="t('dashboard.widget.dragHandle')"
      >
        <svg class="dashboard-widget__icon" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="5" cy="3" r="1.2" />
          <circle cx="5" cy="8" r="1.2" />
          <circle cx="5" cy="13" r="1.2" />
          <circle cx="11" cy="3" r="1.2" />
          <circle cx="11" cy="8" r="1.2" />
          <circle cx="11" cy="13" r="1.2" />
        </svg>
      </button>

      <!-- 모바일 정렬 모드 화살표 -->
      <div v-if="mobileSortMode && !canDrag" class="dashboard-widget__sort-arrows">
        <button
          type="button"
          class="dashboard-widget__arrow"
          :disabled="isFirst"
          :aria-label="t('dashboard.widget.moveUp')"
          @click="$emit('move-up')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3l5 6H3z" /></svg>
        </button>
        <button
          type="button"
          class="dashboard-widget__arrow"
          :disabled="isLast"
          :aria-label="t('dashboard.widget.moveDown')"
          @click="$emit('move-down')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 13L3 7h10z" /></svg>
        </button>
      </div>

      <h3 class="dashboard-widget__title">
        <slot name="title">{{ resolvedTitle }}</slot>
      </h3>

      <div class="dashboard-widget__actions">
        <slot name="actions" />
        <button
          type="button"
          class="dashboard-widget__collapse"
          :aria-label="collapseLabel"
          :title="collapseLabel"
          @click="$emit('toggle-collapse')"
        >
          <svg
            class="dashboard-widget__icon dashboard-widget__chevron"
            :class="{ 'dashboard-widget__chevron--collapsed': collapsed }"
            viewBox="0 0 16 16"
            aria-hidden="true"
          >
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" />
          </svg>
        </button>
      </div>
    </header>

    <!-- ==================== 위젯 본문 ==================== -->
    <div v-show="!collapsed" class="dashboard-widget__body">
      <slot />
    </div>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, ref } from 'vue'
import i18n from '@/locales/i18n'

const t = i18n.global.t
const TOUCH_MOVE_THRESHOLD = 10

const props = defineProps({
  widget: {
    type: Object,
    required: true,
    validator: (v) => v && typeof v.id === 'string' && typeof v.layout === 'string'
  },
  title: { type: String, default: '' },
  titleKey: { type: String, default: '' },
  collapsed: { type: Boolean, default: false },
  canDrag: { type: Boolean, default: false },
  mobileSortMode: { type: Boolean, default: false },
  isFirst: { type: Boolean, default: false },
  isLast: { type: Boolean, default: false },
  longPressMs: { type: Number, default: 500 }
})

const emit = defineEmits(['toggle-collapse', 'long-press', 'move-up', 'move-down'])

const resolvedTitle = computed(() => {
  if (props.title) return props.title
  if (props.titleKey) return t(props.titleKey)
  return props.widget.id
})

const collapseLabel = computed(() =>
  props.collapsed ? t('dashboard.widget.expand') : t('dashboard.widget.collapse')
)

// ── long-press 감지: timer 만료 시 즉시 emit, 도중 이동/뗌은 취소 ──
const longPressTimer = ref(null)
const touchStartPos = ref({ x: 0, y: 0 })

function clearLongPressTimer() {
  if (longPressTimer.value !== null) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

function handleTouchStart(e) {
  if (props.mobileSortMode) return
  const touch = e.touches?.[0]
  if (!touch) return
  touchStartPos.value = { x: touch.clientX, y: touch.clientY }
  clearLongPressTimer()
  longPressTimer.value = setTimeout(() => {
    longPressTimer.value = null
    emit('long-press')
  }, props.longPressMs)
}

function handleTouchMove(e) {
  if (longPressTimer.value === null) return
  const touch = e.touches?.[0]
  if (!touch) return
  const dx = Math.abs(touch.clientX - touchStartPos.value.x)
  const dy = Math.abs(touch.clientY - touchStartPos.value.y)
  if (dx > TOUCH_MOVE_THRESHOLD || dy > TOUCH_MOVE_THRESHOLD) {
    clearLongPressTimer()
  }
}

function handleTouchEnd() {
  clearLongPressTimer()
}
</script>

<style scoped>
/* ========================================
   DashboardWidgetWrapper — 위젯 외곽 카드
   ======================================== */
.dashboard-widget {
  background: var(--color-white, #fff);
  border-radius: var(--radius-xl, 16px);
  border: 1px solid var(--color-gray-100, #E8EAED);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: box-shadow var(--transition-normal, 0.2s ease);
}

.dashboard-widget:hover {
  box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
}

.dashboard-widget--draggable .dashboard-widget__handle {
  cursor: grab;
}

.dashboard-widget--draggable .dashboard-widget__handle:active {
  cursor: grabbing;
}

.dashboard-widget--sort-mode {
  border-color: var(--color-primary, #3B5998);
  box-shadow: 0 0 0 2px rgba(59, 89, 152, 0.15);
}

.dashboard-widget--full { grid-column: 1 / -1; }
.dashboard-widget--half-left { grid-column: 1 / 2; }
.dashboard-widget--half-right { grid-column: 2 / 3; }

/* ===== Header ===== */
.dashboard-widget__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--color-gray-50, #F3F4F6);
}

.dashboard-widget--collapsed .dashboard-widget__header {
  border-bottom: none;
}

.dashboard-widget__handle,
.dashboard-widget__collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-gray-500, #5C616B);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s ease),
    color var(--transition-fast, 0.15s ease);
}

.dashboard-widget__handle:hover,
.dashboard-widget__collapse:hover {
  background: var(--color-gray-50, #F3F4F6);
  color: var(--color-primary, #3B5998);
}

.dashboard-widget__title {
  flex: 1;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.dashboard-widget__title::before {
  content: '';
  width: 4px;
  height: 16px;
  background: var(--color-primary, #3B5998);
  border-radius: var(--radius-sm, 2px);
  flex-shrink: 0;
}

.dashboard-widget__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.dashboard-widget__icon {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.dashboard-widget__chevron {
  transition: transform var(--transition-fast, 0.15s ease);
}

.dashboard-widget__chevron--collapsed {
  transform: rotate(-90deg);
}

/* ===== Mobile Sort Arrows ===== */
.dashboard-widget__sort-arrows {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dashboard-widget__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-600, #424750);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.dashboard-widget__arrow:not(:disabled):hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
}

.dashboard-widget__arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dashboard-widget__arrow svg {
  width: 12px;
  height: 12px;
  fill: currentColor;
}

/* ===== Body ===== */
.dashboard-widget__body {
  padding: 18px;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .dashboard-widget--half-left,
  .dashboard-widget--half-right {
    grid-column: 1 / -1;
  }
  .dashboard-widget__header { padding: 12px 14px; }
  .dashboard-widget__title { font-size: 14px; }
  .dashboard-widget__body { padding: 14px; }
}

/* ===== Tablet (768-1279px) ===== */
/* 프로젝트 기준 PC redesign 진입점은 1280px (useResponsive.BREAKPOINT_PC_REDESIGN). */
@media (min-width: 768px) and (max-width: 1279px) {
  .dashboard-widget--half-left,
  .dashboard-widget--half-right {
    grid-column: 1 / -1;
  }
}
</style>
